import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  afterNextRender,
  computed,
  effect,
  inject,
  Injector,
} from '@angular/core';
import { ProductService } from './services/products.service';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { CustomStoreProduct, ProductItem } from './models/product-item';
import {
  FilterQuery,
  StoreCalculatedPrice,
  StoreCart,
  StoreProductVariant,
} from '@medusajs/types';
import {
  ProductState,
  OrderFilter,
  SortQuery,
  RatingFilter,
  PricingFilter,
  QueryMeilisearch,
} from './models/product-store-models';
import {
  formatPrice,
  getDefaultVariantByProduct,
  getMeiliSearchFilterFormatting,
  sortByDefaultProducts,
  getPriceRangesFromQuery,
  sortProductsByCriteria,
  getDefaultVariantByPriceRanges,
} from './utils/utils';
import { ConfigService } from '@/config/config.service';
import {
  CustomSelectablePriceRange,
  PriceRange,
} from '@/filters/components/pricing-filter/pricing-filter.component';
import { GetInputParams, GetInputParamsMeiliSearch } from '@/models/params';

const initialState: ProductState = {
  product: null,
  products: [],
  count: 0,
  status: 'Loading',
  pageIndex: 0,
  filters: null,
  sorts: null,
  q: '',
  regionId: null,
  currencyCode: null,
  salesChannelId: null,
  cart: null,
  resultCounterInputLabels: null,
  paginationConfig: null,
  isInitialFiltersState: true,
  isRestoringFromUrl: true,
};

export const ProductStore = signalStore(
  { providedIn: 'root' },

  withProps(() => ({
    _productSrv: inject(ProductService),
    _configSrv: inject(ConfigService),
    _injector: inject(Injector), // Necesario para usar effect() dentro de afterNextRender
    _defaultPrice: (price: StoreCalculatedPrice | undefined) => {
      return formatPrice(price);
    },
    _defaultVariant: (
      product: CustomStoreProduct | undefined,
      cart: StoreCart | null
    ) => {
      return getDefaultVariantByProduct(product, cart);
    },
    _defaultVariantWithFiltersSort: (
      product: CustomStoreProduct | undefined,
      cart: StoreCart | null,
      activePriceRanges?: PriceRange[]
    ) => {
      return getDefaultVariantByPriceRanges(product, cart, activePriceRanges);
    },
  })),
  withState<ProductState>(initialState),
  withComputed(state => ({
    limit: computed(() => state.paginationConfig()?.pageSize ?? 24),
    offset: computed(
      () => (state.paginationConfig()?.pageSize ?? 24) * state.pageIndex()
    ),
    priceFilter: computed(() => state.filters()?.prices),
    ratingFilter: computed(() => state.filters()?.rating),
    isInitialFiltersState: computed(() => state.isInitialFiltersState),
    query: computed(() => {
      return {
        filter: getMeiliSearchFilterFormatting(state.filters()),
        sort: state.sorts()
          ? [`${state.sorts()?.orderBy}:${state.sorts()?.order}`]
          : [],
        q: state.q(),
      } as QueryMeilisearch;
    }),
  })),

  withMethods(store => ({
    loadFilters() {
      const ratingFilter = {
        multiple: store._configSrv.getConfigRatingFilter().multiple,
        maxRating: 5,
        selectedRatings: [],
      } as RatingFilter;

      const { options, ...rest } = store._configSrv.getPriceFilterConfig();

      const pricesFilter = {
        ...rest,
        prices: options as CustomSelectablePriceRange[],
        selectedIds: options
          .map((item, index) => (item.selected ? `${index}` : undefined))
          .filter((index): index is string => index !== undefined),
      } as PricingFilter;

      const regionId = store.regionId();
      const currencyCode = store.currencyCode();
      const salesChannelId = store.salesChannelId();
      patchState(store, state => ({
        filters: {
          ...state.filters,
          prices: pricesFilter,
          rating: ratingFilter,
          ...(regionId && { regionId }),
          ...(currencyCode && { currencyCode }),
          ...(salesChannelId && { salesChannelId }),
        },
      }));
    },

    loadSorts() {
      this.setOrder({ order: 'asc', orderBy: 'created_at' });
    },

    loadPaginationConfiguration() {
      patchState(store, {
        paginationConfig: store._configSrv.getPaginationConfig(),
      });
    },

    getProductsList: rxMethod<GetInputParams>(
      pipe(
        tap(() => patchState(store, { status: 'Loading' })),
        switchMap(params => {
          return store._productSrv.getProductsList(params).pipe(
            tap(data => {
              const remappedProducts = data.products.map(item => {
                const defaultVariant = store._defaultVariant(
                  item,
                  store.cart()
                );
                const defaultPrice = store._defaultPrice(
                  defaultVariant?.calculated_price
                );

                return {
                  product: item,
                  defaultVariant,
                  defaultPrice,
                } as ProductItem;
              });

              patchState(store, {
                products: remappedProducts,
                count: data.count,
                status: 'Loaded',
              });
            })
          );
        })
      )
    ),

    getProduct: rxMethod<GetInputParams>(
      pipe(
        tap(() => patchState(store, { status: 'Loading' })),
        switchMap(params => {
          return store._productSrv.getPricesByProductId(params).pipe(
            tapResponse({
              next: data => {
                const defaultVariant = store._defaultVariant(
                  data.product,
                  store.cart()
                );
                const defaultPrice = store._defaultPrice(
                  defaultVariant?.calculated_price
                );

                const product = {
                  product: data.product,
                  defaultVariant,
                  defaultPrice,
                } as ProductItem;

                patchState(store, { product });
              },
              error(error) {
                console.error(error);
              },
              finalize() {
                patchState(store, { status: 'Loaded' });
              },
            })
          );
        })
      )
    ),

    getProductbyMeilisearch: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { status: 'Loading' })),
        switchMap(productId => {
          const meiliSearchParams = {
            query: {
              q: '',
              filter: `id = ${productId}`,
            },
          } as GetInputParamsMeiliSearch;
          return store._productSrv
            .getProductsListFromMeiliSearch(meiliSearchParams)
            .pipe(
              tapResponse({
                next: data => {
                  const defaultVariant = store._defaultVariant(
                    data.product,
                    store.cart()
                  );
                  const defaultPrice = store._defaultPrice(
                    defaultVariant?.calculated_price
                  );

                  const product = {
                    product: data.product,
                    defaultVariant,
                    defaultPrice,
                  } as ProductItem;

                  patchState(store, { product });
                },
                error(error) {
                  console.error(error);
                },
                finalize() {
                  patchState(store, { status: 'Loaded' });
                },
              })
            );
        })
      )
    ),

    getProductsbyMeiliSearch: rxMethod<GetInputParamsMeiliSearch>(
      pipe(
        tap(() => patchState(store, { status: 'Loading' })),
        switchMap(params => {
          return store._productSrv.getProductsListFromMeiliSearch(params).pipe(
            tapResponse({
              next: data => {
                const hits = data.hits as (CustomStoreProduct | undefined)[];
                const activePriceRanges = getPriceRangesFromQuery(params.query);
                const hasActivePriceRanges = activePriceRanges.length > 0;
                const activeFilters = store.query()?.filter;
                const criteria = store.query().sort?.join(',') ?? '';
                const hasRatingFilter = activeFilters?.some(filter =>
                  filter.includes('rating')
                );
                const hasAnyFilterOrSort =
                  hasActivePriceRanges || !!hasRatingFilter || !!criteria;

                const productsItems = hits
                  .map(item => {
                    const defaultVariant = hasActivePriceRanges
                      ? store._defaultVariantWithFiltersSort(
                          item,
                          store.cart(),
                          activePriceRanges
                        )
                      : store._defaultVariant(item, store.cart());

                    const defaultPrice = store._defaultPrice(
                      defaultVariant?.calculated_price
                    );
                    return {
                      product: item,
                      defaultVariant,
                      defaultPrice,
                    } as ProductItem;
                  })
                  .filter(item => item.defaultVariant !== undefined);

                const remappedProducts = hasAnyFilterOrSort
                  ? hasActivePriceRanges
                    ? sortProductsByCriteria(
                        productsItems.filter(
                          item => item.defaultPrice !== undefined
                        ),
                        criteria
                      )
                    : productsItems
                  : sortByDefaultProducts(productsItems);

                patchState(store, {
                  products: remappedProducts,
                  count: data.estimatedTotalHits,
                  status: 'Loaded',
                });
              },
              error(error) {
                console.error(error);
                patchState(store, { status: 'Error', products: [], count: 0 });
              },
            })
          );
        })
      )
    ),

    setProductsPage(pageIndex: number): void {
      patchState(store, { pageIndex, isInitialFiltersState: false });
    },

    resetProductPage() {
      patchState(store, {
        pageIndex: 0,
      });
    },

    // --- Restore methods: update slices of state WITHOUT modifying pageIndex ---
    restoreCategoriesFilter(categoryIds: string[]): void {
      patchState(store, state => ({
        filters: {
          ...state.filters,
          categories: categoryIds,
        },
      }));
    },

    restorePricingFilter(selectedIds: string[]): void {
      if (store.filters()?.prices) {
        patchState(store, state => ({
          filters: {
            ...state.filters!,
            prices: {
              ...state.filters!.prices!,
              selectedIds,
            },
          },
        }));
      }
    },

    restoreSelectedRatings(selectedRatings: number[]): void {
      if (store.filters()?.rating) {
        patchState(store, state => ({
          filters: {
            ...state.filters,
            rating: {
              ...state.filters!.rating!,
              selectedRatings,
            },
          },
        }));
      }
    },

    restoreOrder(sorts: SortQuery): void {
      patchState(store, { sorts });
    },

    setInitialFiltersState(value: boolean): void {
      patchState(store, { isInitialFiltersState: value });
    },

    restoreOrderBy(orderBy: string): void {
      patchState(store, state => ({
        sorts: {
          ...state.sorts,
          orderBy,
        },
      }));
    },

    // A diferencia de los demás métodos "restore", este SÍ escribe pageIndex:
    // es justamente el valor que se está restaurando desde la URL (?page=).
    restorePage(pageIndex: number): void {
      patchState(store, { pageIndex });
    },

    getDefaultPagination() {
      const pageIndex = initialState.pageIndex;
      const pageSize = store._configSrv.getPaginationConfig().pageSize;
      return { pageIndex, pageSize };
    },

    // ─────────────────────────────────────────────────────────────────────────
    // R4, R5: Restauración atómica desde URL.
    // Consolida filtros + pageIndex en un ÚNICO patchState para que el effect
    // de búsqueda en withHooks se dispare exactamente una vez con el estado
    // completo, evitando requests intermedias con pageIndex=0.
    // ─────────────────────────────────────────────────────────────────────────
    restoreFromUrl(params: {
      pageIndex: number;
      priceSelectedIds?: string[];
      selectedRatings?: number[];
      sorts?: SortQuery;
      categoryIds?: string[];
    }): void {
      patchState(store, state => {
        const currentFilters = state.filters;

        const updatedFilters = {
          ...currentFilters,
          // Categorías: guardar la cláusula Meili ya formateada
          // (mismo formato que CategoryFilterService/setCategoriesFilter),
          // porque getMeiliSearchFilterFormatting empuja cada elemento tal cual.
          ...(params.categoryIds !== undefined && {
            categories: params.categoryIds.length
              ? [`categories.id IN [${params.categoryIds.join(',')}]`]
              : [],
          }),
          // Precios: solo si el slice ya existe en el store (cargado por loadFilters)
          ...(params.priceSelectedIds !== undefined &&
            currentFilters?.prices && {
              prices: {
                ...currentFilters.prices,
                selectedIds: params.priceSelectedIds,
              },
            }),
          // Rating: solo si el slice ya existe en el store
          ...(params.selectedRatings !== undefined &&
            currentFilters?.rating && {
              rating: {
                ...currentFilters.rating,
                selectedRatings: params.selectedRatings,
              },
            }),
        };

        return {
          pageIndex: params.pageIndex,
          filters: updatedFilters,
          ...(params.sorts !== undefined && { sorts: params.sorts }),
          // Desbloquea el search effect: estado completo y correcto desde URL
          isRestoringFromUrl: false,
          isInitialFiltersState: false,
        };
      });
    },

    setProducts(products: ProductItem[]): void {
      patchState(store, { products });
    },

    setProduct(product: ProductItem | null): void {
      patchState(store, { product });
    },

    setDefaultVariant(variant: StoreProductVariant | null): void {
      if (store.product()) {
        patchState(store, state => ({
          product: {
            ...state.product!,
            defaultVariant: variant,
          },
        }));
      }
    },

    setSearchQuery(searchQuery: string | null): void {
      patchState(store, {
        q: searchQuery ?? '',
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      });
    },

    setFilters(filters: FilterQuery): void {
      patchState(store, {
        filters,
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      });
    },

    setOrder(sorts: SortQuery): void {
      patchState(store, {
        sorts,
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      });
    },

    setCategoriesFilter(categoryFilter: string[]): void {
      patchState(store, state => ({
        filters: {
          ...state.filters,
          categories: categoryFilter,
        },
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      }));
    },

    setOrderByFilter(orderByFilter: string): void {
      patchState(store, state => ({
        sorts: {
          ...state.sorts,
          orderBy: orderByFilter,
        },
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      }));
    },

    setOrderFilter(orderFilter: OrderFilter): void {
      patchState(store, state => ({
        sorts: {
          ...state.sorts,
          order: orderFilter,
        },
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      }));
    },

    setPricingFilter(priceFilter: PricingFilter): void {
      patchState(store, state => ({
        filters: {
          ...state.filters,
          prices: priceFilter,
        },
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      }));
    },

    setRatingFilter(ratingFilter: RatingFilter): void {
      patchState(store, state => ({
        filters: {
          ...state.filters,
          rating: ratingFilter,
        },
        ...this.getDefaultPagination(),
        isInitialFiltersState: false,
      }));
    },

    setSelectIdsPricing(selectedIds: string[]) {
      if (store.filters() && store.filters()?.prices) {
        patchState(store, state => ({
          filters: {
            ...state.filters!,
            prices: {
              ...state.filters!.prices!,
              selectedIds: selectedIds,
            },
          },
          ...this.getDefaultPagination(),
          isInitialFiltersState: false,
        }));
      }
    },

    removeSelectedPrice(id: number) {
      if (store.filters() && store.filters()?.prices) {
        patchState(store, state => ({
          filters: {
            ...state.filters!,
            prices: {
              ...state.filters!.prices!,
              selectedIds: state.filters!.prices!.selectedIds.filter(
                value => Number(value) !== id
              ),
            },
          },
        }));
      }
    },

    setSelectedRatings(selectedRatings: number[]) {
      if (store.filters() && store.filters()?.rating) {
        patchState(store, state => ({
          filters: {
            ...state.filters,
            rating: {
              ...state.filters!.rating!,
              selectedRatings: selectedRatings,
            },
          },
          ...this.getDefaultPagination(),
          isInitialFiltersState: false,
        }));
      }
    },

    removeSelectedRating(rating: number) {
      if (store.filters() && store.filters()?.rating) {
        patchState(store, state => ({
          filters: {
            ...state.filters,
            rating: {
              ...state.filters!.rating!,
              selectedRatings: state.filters!.rating!.selectedRatings.filter(
                value => value !== rating
              ),
            },
          },
        }));
      }
    },

    setRegionId(regionId: string | null) {
      patchState(store, { regionId });
    },

    setCurrencyCode(currencyCode: string | null) {
      patchState(store, { currencyCode });
    },

    setSalesChannelId(salesChannelId: string | null) {
      patchState(store, { salesChannelId });
    },

    setDefaultConfig(
      regionId: string | null,
      salesChannelId: string | null,
      currencyCode: string | null
    ) {
      patchState(store, state => ({
        regionId,
        currencyCode,
        salesChannelId,
        filters: {
          ...state.filters,
          ...(regionId ? { regionId } : { regiond: undefined }),
          ...(currencyCode ? { currencyCode } : { currencyCode: undefined }),
          ...(salesChannelId
            ? { salesChannelId }
            : { salesChannelId: undefined }),
        },
      }));
    },

    setCart(cart: StoreCart | null) {
      patchState(store, { cart });
    },
  })),

  withHooks({
    onInit(store) {
      // 1. Configuración síncrona (Rápida, segura para SSR)
      store.loadPaginationConfiguration();
      const resultCounterInputLabels =
        store._configSrv.getResultsCounterLabelConfig();
      patchState(store, { resultCounterInputLabels });

      // 2. Lógica DIFERIDA al cliente (Critical Fix)
      // Usamos afterNextRender para que la búsqueda inicial NO ocurra en el servidor.
      // El servidor devolverá la estructura vacía (loading) y el cliente hidratará y buscará.
      afterNextRender(() => {
        // Inicializar filtros si no existen
        if (!store.filters()?.prices || !store.filters()?.rating) {
          store.loadFilters();
        }

        // Reactividad de la búsqueda
        // Creamos el effect aquí para que reaccione a los cambios del store
        // SOLO en el navegador.
        effect(
          () => {
            if (
              store.filters()?.prices === undefined ||
              store.filters()?.rating === undefined
            ) {
              store.loadFilters();
            }

            if (store.isRestoringFromUrl()) {
              return;
            }

            const pageIndex = store.pageIndex();
            const limit = store.limit();
            const offset = pageIndex * limit;
            const { q, filter, sort } = store.query();

            let queryParams = {
              q,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any;

            if (filter && filter.length > 0) {
              queryParams = { filter, ...queryParams };
            }

            if (sort && sort.length > 0) {
              queryParams = { sort, ...queryParams };
            }

            const meiliSearchParams = {
              query: {
                ...queryParams,
                limit,
                offset,
              },
            } as GetInputParamsMeiliSearch;

            if (
              store.filters()?.regionId
              //&& filters()?.currencyCode
              //&& filters()?.salesChannelId
            ) {
              // ─────────────────────────────────────────────────────────────
              // Segunda verificación del gate justo antes de lanzar la request.
              // Captura el estado más reciente del signal en el momento exacto
              // en que se va a disparar la llamada a la API, cubriendo el caso
              // donde el effect fue encolado con isRestoringFromUrl=false pero
              // otro patchState concurrente lo volvió a poner en true antes de
              // que el effect terminara de ejecutarse.
              // ─────────────────────────────────────────────────────────────
              if (store.isRestoringFromUrl()) {
                return;
              }
              store.getProductsbyMeiliSearch(meiliSearchParams);
            }
          },
          { injector: store._injector }
        );
      });
    },
  })
);
